/**
 * PositionsComparisonModal + BulkApplyModal — bulk-flow surfaces.
 *
 * Both consume the same selection state from useSelections('positions').
 *
 *   PositionsComparisonModal — side-by-side comparison of saved positions
 *     across key fields (company, level, market, salary, top requirements).
 *     Lets the user de-select right from the comparison.
 *
 *   BulkApplyModal — destination of the "Build resumes for N positions"
 *     CTA. Today: shows the queued positions + a stub "Generate" button.
 *     Real wiring drops the queue into the resume-builder pipeline (one
 *     tailored resume per position, shared profile evidence pool).
 */
import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  IconButton,
  Snackbar,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import { SAGE, CREAM, WARM } from '../../theme';
import { useSelections } from '../../hooks/useSelections';

const F = "'Outfit', sans-serif";
const SERIF = "'Lora', Georgia, serif";

const formatSalary = (min, max) => {
  if (!min && !max) return '—';
  const toK = (n) => `$${Math.round(n / 1000)}k`;
  if (min && max) return `${toK(min)}–${toK(max)}`;
  return toK(min || max);
};

// ============================================================================
// Comparison
// ============================================================================
export const PositionsComparisonModal = ({ open, onClose, positions }) => {
  const { setState } = useSelections('positions');

  if (!positions || positions.length === 0) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{ sx: { bgcolor: CREAM[50], borderRadius: 3, maxHeight: '92vh' } }}
    >
      <Box sx={{ px: 3, pt: 2.5, pb: 2, borderBottom: `1px solid ${SAGE[200]}`, display: 'flex', alignItems: 'flex-start', gap: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontFamily: F, fontSize: '0.65rem', fontWeight: 700, letterSpacing: 1.6, textTransform: 'uppercase', color: SAGE[500], mb: 0.5, display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <CompareArrowsIcon sx={{ fontSize: 13 }} />
            Compare your saved positions · {positions.length}
          </Typography>
          <Typography sx={{ fontFamily: SERIF, fontSize: '1.2rem', fontWeight: 500, color: SAGE[800], lineHeight: 1.3 }}>
            Side by side
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: SAGE[500] }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={{ px: 3, py: 2, overflowX: 'auto', flex: 1 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: `160px repeat(${positions.length}, minmax(220px, 1fr))`,
            gap: 0,
            border: `1px solid ${SAGE[200]}`, borderRadius: 2, overflow: 'hidden',
          }}
        >
          {/* Column headers (positions) */}
          <Box sx={{ bgcolor: CREAM[100], borderRight: `1px solid ${SAGE[200]}`, p: 1.5 }} />
          {positions.map((p) => (
            <Box key={p.job_id} sx={{ bgcolor: CREAM[100], borderRight: `1px solid ${SAGE[200]}`, p: 1.5, position: 'relative' }}>
              <IconButton
                size="small"
                onClick={() => setState(p.job_id, null)}
                sx={{ position: 'absolute', top: 4, right: 4, color: SAGE[400], '&:hover': { color: SAGE[700] } }}
                title="Remove from comparison"
              >
                <RemoveCircleOutlineIcon sx={{ fontSize: 16 }} />
              </IconButton>
              <Typography sx={{ fontFamily: F, fontSize: '0.7rem', fontWeight: 700, color: SAGE[600] }}>
                {p.company_name}
              </Typography>
              <Typography sx={{ fontFamily: SERIF, fontSize: '0.92rem', fontWeight: 500, color: SAGE[800], lineHeight: 1.25, mt: 0.5, pr: 2 }}>
                {p.title}
              </Typography>
            </Box>
          ))}

          <Row label="Level" positions={positions} render={(p) => p.level || '—'} />
          <Row label="Location" positions={positions} render={(p) => p.location || (p.markets || []).join(', ') || '—'} />
          <Row label="Salary (base)" positions={positions} render={(p) => formatSalary(p.salary_min, p.salary_max)} highlight />
          <Row
            label="What they want"
            positions={positions}
            render={(p) => (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {(p.requirements || []).slice(0, 3).map((r, i) => (
                  <Typography key={i} sx={{ fontFamily: F, fontSize: '0.74rem', color: SAGE[700], lineHeight: 1.4 }}>
                    · {r.length > 90 ? r.slice(0, 87).trim() + '…' : r}
                  </Typography>
                ))}
              </Box>
            )}
          />
          <Row
            label="Posted"
            positions={positions}
            render={(p) => p.updated_at ? new Date(p.updated_at).toLocaleDateString() : '—'}
            isLast
          />
        </Box>
      </Box>
    </Dialog>
  );
};

const Row = ({ label, positions, render, highlight, isLast }) => (
  <>
    <Box
      sx={{
        bgcolor: CREAM[50], borderRight: `1px solid ${SAGE[200]}`,
        borderTop: `1px solid ${SAGE[100]}`, borderBottom: isLast ? 'none' : 'none',
        p: 1.5, display: 'flex', alignItems: 'flex-start',
      }}
    >
      <Typography sx={{ fontFamily: F, fontSize: '0.65rem', fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: SAGE[500] }}>
        {label}
      </Typography>
    </Box>
    {positions.map((p) => (
      <Box
        key={p.job_id}
        sx={{
          bgcolor: highlight ? `${SAGE[100]}55` : CREAM[50],
          borderRight: `1px solid ${SAGE[200]}`,
          borderTop: `1px solid ${SAGE[100]}`,
          p: 1.5, fontFamily: F, fontSize: '0.82rem', color: SAGE[700],
        }}
      >
        {render(p)}
      </Box>
    ))}
  </>
);

// ============================================================================
// Bulk apply
// ============================================================================
export const BulkApplyModal = ({ open, onClose, positions }) => {
  const [working, setWorking] = useState(false);
  const [done, setDone] = useState(false);
  const [snack, setSnack] = useState(null);

  if (!positions || positions.length === 0) return null;

  const handleGenerate = () => {
    setWorking(true);
    setTimeout(() => {
      setWorking(false);
      setDone(true);
      setSnack({ severity: 'success', text: `${positions.length} tailored resumes drafted (demo).` });
    }, 1400);
  };

  const handleClose = () => {
    setDone(false);
    setWorking(false);
    onClose();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { bgcolor: CREAM[50], borderRadius: 3, maxHeight: '90vh' } }}
      >
        <Box sx={{ px: 3, pt: 2.5, pb: 2, borderBottom: `1px solid ${SAGE[200]}`, display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontFamily: F, fontSize: '0.65rem', fontWeight: 700, letterSpacing: 1.6, textTransform: 'uppercase', color: SAGE[500], mb: 0.5, display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <AutoAwesomeIcon sx={{ fontSize: 13 }} />
              Bulk resume builder · preview
            </Typography>
            <Typography sx={{ fontFamily: SERIF, fontSize: '1.2rem', fontWeight: 500, color: SAGE[800], lineHeight: 1.3 }}>
              Draft tailored resumes for {positions.length} {positions.length === 1 ? 'position' : 'positions'}
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small" sx={{ color: SAGE[500] }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ px: 3, py: 2.5, overflowY: 'auto', flex: 1, maxHeight: '65vh' }}>
          <Typography sx={{ fontFamily: F, fontSize: '0.85rem', color: SAGE[600], mb: 2, lineHeight: 1.6 }}>
            We'll draft a tailored resume for each posting using your profile + the role's requirements. Each draft sits in your queue for review and editing before submission.
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1 }}>
            {positions.map((p) => (
              <Box
                key={p.job_id}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1.5,
                  px: 1.75, py: 1.25, borderRadius: 1.5,
                  bgcolor: '#fff', border: `1.5px solid ${SAGE[200]}`,
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ fontFamily: F, fontSize: '0.78rem', fontWeight: 700, color: SAGE[700] }}>
                      {p.company_name}
                    </Typography>
                    {p.level && (
                      <Chip
                        label={p.level} size="small"
                        sx={{ bgcolor: SAGE[100], color: SAGE[700], fontFamily: F, fontSize: '0.62rem', fontWeight: 600, height: 18 }}
                      />
                    )}
                  </Box>
                  <Typography sx={{ fontFamily: SERIF, fontSize: '0.92rem', fontWeight: 500, color: SAGE[800], lineHeight: 1.3 }}>
                    {p.title}
                  </Typography>
                </Box>
                {done ? (
                  <Chip
                    label="Drafted"
                    size="small"
                    sx={{ bgcolor: '#3d7a4a18', color: '#3d7a4a', fontFamily: F, fontSize: '0.65rem', fontWeight: 700, height: 22 }}
                  />
                ) : working ? (
                  <CircularProgress size={14} sx={{ color: SAGE[500] }} />
                ) : (
                  <Chip
                    label="Queued"
                    size="small"
                    sx={{ bgcolor: WARM.accent + '22', color: WARM.accent, fontFamily: F, fontSize: '0.65rem', fontWeight: 700, height: 22 }}
                  />
                )}
              </Box>
            ))}
          </Box>
        </Box>

        <Box sx={{ px: 3, py: 2, borderTop: `1px solid ${SAGE[200]}`, bgcolor: CREAM[100], display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontFamily: F, fontSize: '0.74rem', color: SAGE[500], fontStyle: 'italic' }}>
              Drafts land in your queue for review · no auto-submit
            </Typography>
          </Box>
          <Button onClick={handleClose} sx={{ fontFamily: F, fontSize: '0.78rem', fontWeight: 600, color: SAGE[600], textTransform: 'none', '&:hover': { bgcolor: 'transparent', color: SAGE[800] } }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleGenerate}
            disabled={working || done}
            startIcon={working ? <CircularProgress size={14} sx={{ color: CREAM[50] }} /> : <AutoAwesomeIcon sx={{ fontSize: 16 }} />}
            sx={{
              fontFamily: F, fontSize: '0.82rem', fontWeight: 600,
              bgcolor: SAGE[700], color: CREAM[50], textTransform: 'none', borderRadius: 2,
              '&:hover': { bgcolor: SAGE[800] },
              '&.Mui-disabled': { bgcolor: SAGE[300], color: CREAM[100] },
            }}
          >
            {working ? 'Drafting…' : done ? 'Drafts ready' : `Generate ${positions.length} resumes`}
          </Button>
        </Box>
      </Dialog>

      <Snackbar
        open={!!snack}
        autoHideDuration={4500}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snack && (
          <Alert severity={snack.severity} onClose={() => setSnack(null)} sx={{ width: '100%' }}>
            {snack.text}
          </Alert>
        )}
      </Snackbar>
    </>
  );
};
